<template>
  <div class="material-check-page frame-page cji3-cost-page" :class="{ 'is-loading': loading }">
    <div class="page-intro">
      <p>
        上传 SAP cji3 成本明细（.xls / .xlsx / .mht / .mhtml），按 WBS 汇总项目成本：
        PostedUofM 为空为其他成本，为 AU 为服务成本，其余为物资成本。
        项目编码：WBS 以 18 开头取前 14 位，否则取前 12 位。
      </p>
    </div>

    <input ref="fileInput" type="file" hidden accept=".mht,.mhtml,.xls,.xlsx" @change="onFileSelected" />

    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">1</span>
          <span>cji3 成本明细</span>
          <span v-if="displayName" class="tag tag-done">已上传</span>
          <span v-else class="tag tag-muted">待选择</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" class="drop-btn-clear" @click="doClear">清除</Button>
          <Button size="s" color="primary" @click="pickFile">浏览</Button>
        </div>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !displayName,
          'is-dragover': dragOver,
          'is-filled': displayName,
        }"
        @click="pickFile"
        @dragenter.prevent="dragOver = true"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
      >
        <div class="drop-zone-body drop-zone-body-compact">
          <div class="drop-icon" :class="{ 'is-uploaded': displayName }">
            <span v-if="displayName" class="drop-check">✓</span>
            <span v-else>cji3</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !displayName }">
              {{ displayName || '选择或拖拽 cji3 成本明细文件' }}
            </div>
            <div class="drop-hint">
              <template v-if="displayName">
                已分析 {{ rowCount }} 行明细，汇总 {{ projectCount }} 个项目
              </template>
              <template v-else>需含 WBS 元素、业务货币值、PostedUofM 列</template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">2</span>
          <span>生成结果</span>
          <span v-if="lastResult" class="tag tag-done">已生成</span>
          <span v-else class="tag tag-muted">待导出</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" class="drop-btn-clear" @click="doClear">清除</Button>
          <Button color="primary" size="s" :disabled="!displayName" @click="doExport">导出</Button>
        </div>
      </div>
      <div class="drop-zone result-zone" :class="{ 'is-empty': !lastResult, 'is-filled': lastResult }">
        <div class="drop-zone-body drop-zone-body-compact">
          <div class="drop-icon" :class="{ 'is-uploaded': lastResult }">
            <span v-if="lastResult" class="drop-check">✓</span>
            <span v-else>结果</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !lastResult }">
              <template v-if="lastResult">{{ lastResult.filename }}</template>
              <template v-else>上传并分析后可导出汇总 Excel</template>
            </div>
            <div class="drop-hint">
              <template v-if="lastResult">
                共 {{ lastResult.rowCount }} 行
                <template v-if="lastResult.path"> · 已用 Excel 打开</template>
              </template>
              <template v-else>按项目编码汇总物资 / 服务 / 其他成本</template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="previewRows.length" class="panel">
      <div class="panel-title">
        <span class="step">3</span>
        <span>预览（前 20 行）</span>
      </div>
      <div class="preview-table-wrap">
        <table class="preview-table">
          <thead>
            <tr>
              <th class="col-seq">序号</th>
              <th>项目编码</th>
              <th class="col-name">项目名称</th>
              <th class="col-num">物资成本</th>
              <th class="col-num">服务成本</th>
              <th class="col-num">其他成本</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in previewRows" :key="row['序号']">
              <td class="col-seq">{{ row['序号'] }}</td>
              <td>{{ row['项目编码'] }}</td>
              <td class="col-name">{{ row['项目名称'] || '—' }}</td>
              <td class="col-num">{{ formatMoney(row['物资成本']) }}</td>
              <td class="col-num">{{ formatMoney(row['服务成本']) }}</td>
              <td class="col-num">{{ formatMoney(row['其他成本']) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { confirm, message } from 'heyui';
import { cji3CostApi as api } from '@/api/cji3Cost';
import '@/views/material-check/material-check.less';

const FILE_EXTS = ['.mht', '.mhtml', '.xls', '.xlsx'];

const loading = ref(false);
const displayName = ref('');
const rowCount = ref(0);
const projectCount = ref(0);
const previewRows = ref([]);
const fileInput = ref(null);
const dragOver = ref(false);
const lastResult = ref(null);
const uploadedFile = ref(null);

function formatMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0.00';
  return num.toFixed(2);
}

function matchExtension(name) {
  const lower = (name || '').toLowerCase();
  return FILE_EXTS.some((ext) => lower.endsWith(ext));
}

async function withLoading(fn) {
  loading.value = true;
  try {
    return await fn();
  } finally {
    loading.value = false;
  }
}

function showError(msg) {
  confirm({
    type: 'error',
    title: '错误',
    content: msg || '操作失败',
  });
}

function applyLoadResult(data) {
  displayName.value = data.displayName || displayName.value || '';
  rowCount.value = data.rowCount || 0;
  projectCount.value = data.projectCount || 0;
  previewRows.value = data.previewRows || [];
}

function pickFile() {
  fileInput.value?.click();
}

async function uploadSelected(file) {
  if (!file) return;
  if (!matchExtension(file.name)) {
    showError('仅支持 .mht / .mhtml / .xls / .xlsx');
    return;
  }
  uploadedFile.value = file;
  await withLoading(async () => {
    const resp = await api.upload(file);
    if (!resp.ok) {
      showError(resp.error);
      return;
    }
    applyLoadResult(resp);
    message.success(`分析完成：${projectCount.value} 个项目`);
  });
}

function onFileSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  uploadSelected(file);
}

function onDrop(event) {
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  uploadSelected(file);
}

async function ensureAnalyzed() {
  const state = await api.getState();
  if (state.ok && state.hasFile) {
    applyLoadResult(state);
    return true;
  }
  if (uploadedFile.value) {
    const resp = await api.upload(uploadedFile.value);
    if (!resp.ok) {
      showError(resp.error || '请重新上传 cji3 文件');
      return false;
    }
    applyLoadResult(resp);
    return true;
  }
  showError('请先上传并分析 cji3 文件');
  return false;
}

async function doExport() {
  await withLoading(async () => {
    const ready = await ensureAnalyzed();
    if (!ready) return;
    const resp = await api.export();
    if (!resp.ok) {
      showError(resp.error);
      return;
    }
    lastResult.value = {
      filename: resp.filename,
      path: resp.path || '',
      rowCount: resp.rowCount,
      downloadUrl: api.downloadUrl(resp.filename),
    };
    // 后端已 os.startfile 打开 Excel，与「数量求和/条件求和」一致，不再弹出浏览器另存为
    message.success(resp.path ? `导出完成：${resp.path}` : 'Excel 已生成并打开');
  });
}

async function doClear() {
  await withLoading(async () => {
    await api.reset();
    uploadedFile.value = null;
    displayName.value = '';
    rowCount.value = 0;
    projectCount.value = 0;
    previewRows.value = [];
    lastResult.value = null;
  });
}

onMounted(async () => {
  const resp = await api.getState();
  if (resp.ok && resp.hasFile) {
    applyLoadResult(resp);
  } else if (displayName.value && !resp.hasFile) {
    // 后端重启后前端残留展示，清空避免误导
    displayName.value = '';
    rowCount.value = 0;
    projectCount.value = 0;
    previewRows.value = [];
  }
});
</script>

<style lang="less" scoped>
.panel-title-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-title-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.panel-title-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}

.drop-zone-body-compact {
  grid-template-columns: auto 1fr;
}

.result-zone {
  cursor: default;

  &:hover {
    border-color: #d8dee6;
    background: #fafbfc;
  }

  &.is-filled:hover {
    border-color: #d0d7e2;
    background: #fff;
  }
}

.preview-table-wrap {
  overflow: auto;
  max-height: 420px;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #eef1f5;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    background: #f7f9fc;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  th.col-seq,
  td.col-seq {
    width: 64px;
  }

  th.col-num,
  td.col-num {
    font-variant-numeric: tabular-nums;
  }
}

.download-link {
  margin-left: 8px;
}
</style>
